// ENG-3105 U1 helpers for Recapture email authoring. Branch only on U1a's
// exact static 409 codes and the allowlisted 422 envelope — never parse
// free-form messages.

import {
  PRESENTATION_FIELD_KEYS,
  presentationValuesFrom,
} from './Templates/presentationFields';

export const RECAPTURE_EMAIL_MAX_BYTES = 2 * 1024 * 1024;

export const CONFLICT_CODES = {
  STALE_REVISION: 'stale_revision',
};

const CONFLICT_CODE_SET = new Set(Object.values(CONFLICT_CODES));

export const VALIDATION_ERROR_NAME = 'invalid_recapture_email_input';
export const CONFLICT_ERROR_NAME = 'recapture_email_conflict';
export const ACTIVATION_UNAVAILABLE = 'activation_unavailable';

export const ROLLOUT_PENDING_COPY = 'Email schedule and campaign activation is pending rollout.';

export const utf8ByteLength = (value) => {
  const text = value == null ? '' : String(value);
  if (typeof TextEncoder === 'function') {
    return new TextEncoder().encode(text).length;
  }
  return unescape(encodeURIComponent(text)).length;
};

export const jsonEnvelopeByteLength = (envelope) => utf8ByteLength(JSON.stringify(envelope));

export const envelopeExceedsLimit = (envelope) => (
  jsonEnvelopeByteLength(envelope) > RECAPTURE_EMAIL_MAX_BYTES
);

export const recaptureEmailPayloadFromAction = (actionOrError) => (
  actionOrError?.payload?.response?.data
  || actionOrError?.response?.data
  || null
);

export const recaptureEmailHttpStatus = (actionOrError) => (
  actionOrError?.payload?.response?.status
  || actionOrError?.response?.status
  || null
);

export const recaptureEmailConflictCode = (actionOrError) => {
  const payload = recaptureEmailPayloadFromAction(actionOrError);
  if (!payload || payload.error !== CONFLICT_ERROR_NAME) return null;
  return CONFLICT_CODE_SET.has(payload.code) ? payload.code : null;
};

export const recaptureEmailValidationCode = (actionOrError) => {
  const payload = recaptureEmailPayloadFromAction(actionOrError);
  if (!payload || payload.error !== VALIDATION_ERROR_NAME) return null;
  return typeof payload.code === 'string' ? payload.code : null;
};

export const isActivationUnavailableError = (actionOrError) => (
  recaptureEmailValidationCode(actionOrError) === ACTIVATION_UNAVAILABLE
);

export const isAccessDeniedStatus = (actionOrError) => recaptureEmailHttpStatus(actionOrError) === 403;

export const isOversizedStatus = (actionOrError) => recaptureEmailHttpStatus(actionOrError) === 413;

export const extractRecaptureEmailUserError = (actionOrError) => {
  if (isAccessDeniedStatus(actionOrError)) {
    return 'You do not have access to Recapture email.';
  }
  if (isOversizedStatus(actionOrError)) {
    return 'This email is too large to save or preview (2 MiB limit).';
  }
  if (isActivationUnavailableError(actionOrError)) {
    return ROLLOUT_PENDING_COPY;
  }
  const conflict = recaptureEmailConflictCode(actionOrError);
  if (conflict === CONFLICT_CODES.STALE_REVISION) {
    return 'This template was updated elsewhere. Keep your draft or reload the current revision.';
  }
  const payload = recaptureEmailPayloadFromAction(actionOrError);
  if (payload?.error === VALIDATION_ERROR_NAME) {
    return 'Email template input is invalid.';
  }
  return 'An unexpected error occurred';
};

export const buildTemplateEnvelope = (templateData) => ({
  recapture_email_template: templateData,
});

export const buildSettingsEnvelope = (settingsData) => ({
  recapture_email_setting: settingsData,
});

// Preview requires all four keys as explicitly resolved values (JSON null is
// allowed). Inherit flags substitute the loaded settings value; preview never
// infers omitted update values.
export const resolvePresentationForPreview = ({
  draftValues,
  useOrgDefault,
  settings,
}) => {
  const defaults = presentationValuesFrom(settings, null);
  return PRESENTATION_FIELD_KEYS.reduce((acc, field) => {
    if (useOrgDefault && useOrgDefault[field]) {
      acc[field] = Object.prototype.hasOwnProperty.call(defaults, field)
        ? defaults[field]
        : null;
    } else if (draftValues && Object.prototype.hasOwnProperty.call(draftValues, field)) {
      acc[field] = draftValues[field];
    } else {
      acc[field] = null;
    }
    return acc;
  }, {});
};

export const resolvePresentationForSave = ({ draftValues, useOrgDefault }) => (
  PRESENTATION_FIELD_KEYS.reduce((acc, field) => {
    if (useOrgDefault && useOrgDefault[field]) {
      acc[field] = null;
      return acc;
    }
    const value = draftValues && draftValues[field];
    acc[field] = (value != null && value !== '') ? value : null;
    return acc;
  }, {})
);
